/**
 * User interaction tracking for behavioral bot detection.
 *
 * @module bot-detection/interaction-tracker
 */

export interface UserInteractionMetrics {
  mouseMovements: number;
  scrolls: number;
  clicks: number;
  keystrokes: number;
  /** Seconds since the tracker was constructed, not since page load. */
  timeSpent: number;
}

export interface HumanBehaviorScore {
  score: number;
  hasHumanBehavior: boolean;
  details: UserInteractionMetrics;
}

/**
 * Tracks mouse/scroll/click/keyboard activity for the life of the page.
 *
 * Singleton because the listeners must be attached exactly once and the counts
 * are session-wide. Construct it as early as possible (via
 * {@link initInteractionTracking}) so `sessionStartTime` approximates page load
 * rather than whenever the first consumer happened to ask for a score.
 */
export class UserInteractionTracker {
  private static instance: UserInteractionTracker;
  private totalInteractions = 0;
  private sessionStartTime = Date.now();
  private mouseMovementCount = 0;
  private keystrokeCount = 0;
  private scrollCount = 0;
  private clickCount = 0;

  private constructor() {
    if (typeof window !== 'undefined') {
      this.initializeEventListeners();
    }
  }

  public static getInstance(): UserInteractionTracker {
    if (!UserInteractionTracker.instance) {
      UserInteractionTracker.instance = new UserInteractionTracker();
    }
    return UserInteractionTracker.instance;
  }

  // All listeners are passive: they must never delay scrolling or input, since
  // this is analytics instrumentation on the critical interaction path.
  private initializeEventListeners(): void {
    window.addEventListener(
      'mousemove',
      () => {
        this.mouseMovementCount++;
        this.totalInteractions++;
      },
      { passive: true },
    );

    // Debounced so one flick of the wheel counts as one scroll, keeping the
    // scroll count on the same order of magnitude as clicks/keystrokes below.
    let scrollDebounceTimeout: ReturnType<typeof setTimeout>;
    window.addEventListener(
      'scroll',
      () => {
        clearTimeout(scrollDebounceTimeout);
        scrollDebounceTimeout = setTimeout(() => {
          this.scrollCount++;
          this.totalInteractions++;
        }, 100);
      },
      { passive: true },
    );

    window.addEventListener(
      'click',
      () => {
        this.clickCount++;
        this.totalInteractions++;
      },
      { passive: true },
    );

    window.addEventListener(
      'keydown',
      () => {
        this.keystrokeCount++;
        this.totalInteractions++;
      },
      { passive: true },
    );
  }

  /**
   * Scores how human the session looks so far, 0-1 (higher = more human).
   *
   * Time alone can reach 0.7, so an idle-but-present tab would pass the score
   * threshold; the extra `totalInteractions > 3` requirement below is what
   * keeps a parked headless page from qualifying as human.
   */
  public calculateHumanBehaviorScore(): HumanBehaviorScore {
    const timeSpentSeconds = (Date.now() - this.sessionStartTime) / 1000;

    let behaviorScore = 0;

    if (timeSpentSeconds > 3) behaviorScore += 0.3;
    if (timeSpentSeconds > 10) behaviorScore += 0.2;
    if (timeSpentSeconds > 30) behaviorScore += 0.2;

    // Thresholds rather than counts: crossing them once is the signal, so a
    // long session cannot inflate the score past the weights.
    if (this.mouseMovementCount > 5) behaviorScore += 0.2;
    if (this.scrollCount > 2) behaviorScore += 0.2;
    if (this.clickCount > 0) behaviorScore += 0.2;
    if (this.keystrokeCount > 0) behaviorScore += 0.1;

    behaviorScore = Math.min(behaviorScore, 1.0);

    const hasHumanBehavior = behaviorScore >= 0.5 && this.totalInteractions > 3;

    return {
      score: behaviorScore,
      hasHumanBehavior,
      details: {
        mouseMovements: this.mouseMovementCount,
        scrolls: this.scrollCount,
        clicks: this.clickCount,
        keystrokes: this.keystrokeCount,
        timeSpent: timeSpentSeconds,
      },
    };
  }

  /**
   * Starts a fresh measurement window. Listeners stay attached — only the
   * counters and the time origin reset.
   */
  public resetSessionTracking(): void {
    this.totalInteractions = 0;
    this.sessionStartTime = Date.now();
    this.mouseMovementCount = 0;
    this.keystrokeCount = 0;
    this.scrollCount = 0;
    this.clickCount = 0;
  }
}

/**
 * Eagerly constructs the interaction tracker singleton.
 *
 * Call this from the app/analytics initialization path (as close to page load
 * as possible) so that `sessionStartTime` and the interaction listeners start
 * at page load rather than lazily on the first tracked event — otherwise
 * time-based signals (e.g. the `tooFast` flag) measure from the wrong origin.
 */
export function initInteractionTracking(): void {
  UserInteractionTracker.getInstance();
}
